import { Box, Button, Link } from "@/components";
import { Tooltip } from "@/components/shared/Tooltip";
import { TOKEN_CONFIG } from "@/config";
import { useLockPositions, useStakingPosition, useWalletBalances } from "@/hooks";
import { formatDisplayCurrency } from "@/utils/format-currency";
import { COMMON_TOKEN_IDS } from "@/types";
import { MdInfoOutline } from "react-icons/md";
import { memo, useMemo } from "react";
import { formatUnits } from "viem";
import type { PortfolioTokenCard } from "./usePortfolioTokenCards";

const TOKEN_TITLE_TOOLTIPS = {
    [COMMON_TOKEN_IDS.IUSD]:
        "iUSD is the native stablecoin minted on infiniFi. Unallocated iUSD can be staked or locked for yield.",
    [COMMON_TOKEN_IDS.SIUSD]: "siUSD represents your staked iUSD position. Staking earns yield from protocol revenue.",
    [COMMON_TOKEN_IDS.LIUSD]: "liUSD represents locked iUSD. Longer lock durations earn higher yield and voting power.",
} as const;

const usePortfolioFeaturedCard = (tokenCard: PortfolioTokenCard) => {
    const { tokenBalances } = useWalletBalances();
    const { lockedPositions } = useLockPositions();
    const { stakingPosition } = useStakingPosition();

    const { tokenId, slots } = tokenCard;
    const { buttons, footer } = slots ?? {};

    const tokenConfig = TOKEN_CONFIG[tokenId as keyof typeof TOKEN_CONFIG];

    const isLiUsd = tokenId === COMMON_TOKEN_IDS.LIUSD;
    const isSiUsd = tokenId === COMMON_TOKEN_IDS.SIUSD;

    const { displayBalance, displayUSD } = useMemo(() => {
        const defaultBalance = tokenBalances[tokenId]?.tokenBalance;

        if (isSiUsd) {
            return {
                displayBalance: defaultBalance,
                displayUSD: stakingPosition?.stakedBalance,
            };
        }

        if (!isLiUsd) {
            return {
                displayBalance: defaultBalance,
                displayUSD: defaultBalance,
            };
        }

        const liUsdTotal = lockedPositions.reduce((acc, position) => acc + BigInt(position.liUSDBalance || 0), 0n);
        const liUsdUsdTotal = lockedPositions.reduce((acc, position) => acc + BigInt(position.iUSDBalance || 0), 0n);

        return {
            displayBalance: formatUnits(liUsdTotal, 18),
            displayUSD: formatUnits(liUsdUsdTotal, 18),
        };
    }, [isLiUsd, isSiUsd, lockedPositions, stakingPosition, tokenBalances, tokenId]);

    const lockedPositionBalances = useMemo(
        () =>
            isLiUsd
                ? lockedPositions
                      .filter((position) => +position.liUSDBalance > 0)
                      .map(({ lockDuration, liUSDBalance }) => {
                          const liUSDEthers = formatUnits(BigInt(liUSDBalance), 18);
                          return {
                              symbol: `liUSD-${lockDuration}`,
                              balance: Number(liUSDEthers),
                          };
                      })
                : [],
        [isLiUsd, lockedPositions],
    );

    return {
        tokenConfig,
        isLiUsd,
        buttons,
        footer,
        displayBalance,
        displayUSD,
        lockedPositionBalances,
    };
};

export const PortfolioFeaturedTokenCard = memo((tokenCard: PortfolioTokenCard) => {
    const { tokenConfig, isLiUsd, buttons, footer, displayBalance, displayUSD, lockedPositionBalances } =
        usePortfolioFeaturedCard(tokenCard);
    const footerLinks = Array.isArray(footer?.links) ? footer.links : [];
    const titleTooltip = TOKEN_TITLE_TOOLTIPS[tokenCard.tokenId as keyof typeof TOKEN_TITLE_TOOLTIPS];

    const title = (
        <h3 className="text-primary text-[16px] leading-[24px] flex items-center justify-start font-medium w-fit">
            {tokenConfig.symbol}
            {titleTooltip ? (
                <Tooltip title={titleTooltip} arrow placement="top">
                    <span className="inline-flex items-center">
                        <MdInfoOutline className="w-4 h-4 ms-1 text-dark cursor-help" />
                    </span>
                </Tooltip>
            ) : null}
        </h3>
    );

    const tokenDisplay = (
        <span className="flex flex-col my-6 gap-2 w-fit">
            <span className="text-[32px] text-primary leading-[28px] font-normal">
                {formatDisplayCurrency(displayBalance, 2) ?? "0"} {tokenConfig.symbol}
                {isLiUsd && <MdInfoOutline className="w-5 h-5 inline ms-2 mb-1 text-dark" />}
            </span>
            <span className="text-[14px] leading-[13px] text-muted">≈ ${formatDisplayCurrency(displayUSD, 2)}</span>
        </span>
    );

    return (
        <Box card>
            {/* title */}
            {title}
            <span className="text-[11px] leading-[12px] text-dark">{tokenConfig.name}</span>
            {/* amount display */}
            {isLiUsd ? (
                <Tooltip
                    title={
                        <ul className="my-2 ml-2 mr-4 text-[12px] leading-[16px] text-dark">
                            {lockedPositionBalances.map((lockedBalance, index) => (
                                <li key={`${tokenConfig.symbol}-locked-balance-${index}`}>
                                    {lockedBalance.symbol}: {formatDisplayCurrency(lockedBalance.balance, 2)}
                                </li>
                            ))}
                        </ul>
                    }
                    placement="top-end"
                >
                    {tokenDisplay}
                </Tooltip>
            ) : (
                tokenDisplay
            )}
            {buttons && (
                <span className="flex flex-col gap-2">
                    {"primary" in buttons && (
                        <Button variant="primary" size="md" fullWidth onClick={buttons.primary?.onClick}>
                            {buttons.primary?.label ? buttons.primary.label : `Primary Action`}
                        </Button>
                    )}
                    {"secondary" in buttons && (
                        <Button
                            variant="secondary"
                            size="md"
                            fullWidth
                            onClick={buttons.secondary?.onClick}
                            disabled={buttons.secondary?.disabled}
                        >
                            {buttons.secondary?.label ? buttons.secondary.label : `Secondary Action`}
                        </Button>
                    )}
                </span>
            )}
            {footerLinks.length > 0 && (
                <div className="flex justify-between mt-4">
                    {footerLinks.map((link, index) =>
                        "href" in link ? (
                            <Link
                                key={index}
                                href={link.href}
                                className="text-[12px] text-primary underline transition-all duration-200 hover:saturate-200"
                            >
                                {link.label}
                            </Link>
                        ) : "onClick" in link ? (
                            <button
                                key={index}
                                onClick={link.onClick}
                                className="text-[12px] text-primary underline transition-all duration-200 hover:saturate-200"
                            >
                                {link.label}
                            </button>
                        ) : null,
                    )}
                </div>
            )}
        </Box>
    );
});

PortfolioFeaturedTokenCard.displayName = "PortfolioFeaturedTokenCard";
